import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Maximize } from 'lucide-react'
import { useAuthStore } from '@/store'
import type { EventConfig, SessionPhoto } from '@/types'
import QRCode from 'qrcode'
import { cn } from '@/lib/utils'

export default function KioskPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { user, initialized: authInitialized, checkAuth } = useAuthStore()
  
  const [event, setEvent] = useState<EventConfig | null>(null)
  const [photos, setPhotos] = useState<SessionPhoto[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [qrData, setQrData] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [unauthorized, setUnauthorized] = useState(false)
  const lastPhotoId = useRef<string | null>(null)

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`)
      })
    } else {
      document.exitFullscreen()
    }
  }

  // Fetch photos helper
  const fetchPhotos = useCallback(async (ev: EventConfig) => {
    if (!ev) return
    try {
      const res = await fetch(`/api/gallery?eventId=${eventId}`)
      if (!res.ok) return
      const sessions = await res.json()
      
      let allPhotos: SessionPhoto[] = []
      for (const session of sessions) {
        let sessionPhotos = session.photos || []
        
        if (ev.kiosk?.contentFilter === 'separate') {
          sessionPhotos = sessionPhotos.filter((p: any) => p.type !== 'layout')
        } else if (ev.kiosk?.contentFilter === 'layouts') {
          sessionPhotos = sessionPhotos.filter((p: any) => p.type === 'layout')
        }
        
        allPhotos.push(...sessionPhotos)
      }
      
      const order = ev.kiosk?.sortOrder || 'newestFirst'
      if (order === 'newestFirst') {
        allPhotos.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      } else if (order === 'oldestFirst') {
        allPhotos.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      } else if (order === 'random') {
        allPhotos.sort(() => Math.random() - 0.5)
      }
      
      if (allPhotos.length > 0) {
        if (order === 'newestFirst' && lastPhotoId.current && allPhotos[0].id !== lastPhotoId.current) {
          if (ev.kiosk?.refreshOnNewPhoto !== false) {
            setCurrentIndex(0)
          }
        }
        lastPhotoId.current = allPhotos[0].id
      }
      
      setPhotos(allPhotos)
    } catch (err) {
      console.error('Failed to fetch kiosk photos:', err)
    }
  }, [eventId])

  // Auth and event config fetch
  const fetchEventConfig = useCallback(async () => {
    try {
      const res = await fetch(`/api/events/${eventId}`)
      if (!res.ok) {
        setUnauthorized(true)
        setLoading(false)
        return
      }
      const data = await res.json()
      const ev = data.config as EventConfig

      // Verify access control
      const isLoggedIn = !!user
      const publicAccess = ev.kiosk?.publicAccess || 'disabled'
      const expiresAt = ev.kiosk?.publicAccessExpiresAt
      const publicToken = ev.kiosk?.publicToken

      let isAccessAllowed = isLoggedIn

      if (!isAccessAllowed && publicAccess === 'enabled' && publicToken && token === publicToken) {
        if (!expiresAt) {
          isAccessAllowed = true
        } else {
          const expiryTime = new Date(expiresAt).getTime()
          if (expiryTime > Date.now()) {
            isAccessAllowed = true
          }
        }
      }

      if (!isAccessAllowed) {
        setUnauthorized(true)
        setLoading(false)
        return
      }

      setEvent(ev)
      const url = `${window.location.origin}/gallery?eventId=${ev.id}`
      QRCode.toDataURL(url, { margin: 1, width: 200 }).then(setQrData)

      await fetchPhotos(ev)
      setLoading(false)
    } catch (err) {
      console.error('Failed to load event for kiosk:', err)
      setUnauthorized(true)
      setLoading(false)
    }
  }, [eventId, user, token, fetchPhotos])

  useEffect(() => {
    if (!authInitialized) {
      checkAuth()
      return
    }

    fetchEventConfig()
  }, [authInitialized, fetchEventConfig])

  useEffect(() => {
    if (unauthorized) {
      navigate('/login', { replace: true })
    }
  }, [unauthorized, navigate])

  // Polling for new photos
  useEffect(() => {
    if (!event) return
    const refreshInterval = (event.kiosk?.autoRefreshInterval !== undefined ? event.kiosk.autoRefreshInterval : 10) * 1000
    if (refreshInterval <= 0) return
    
    const interval = setInterval(() => {
      fetchPhotos(event)
    }, refreshInterval)
    return () => clearInterval(interval)
  }, [fetchPhotos, event])

  // Slideshow rotation
  useEffect(() => {
    if (photos.length === 0 || !event) return
    const loop = event.kiosk?.loopSlideshow !== false
    const slideInterval = (event.kiosk?.interval || 10) * 1000
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextIndex = prev + 1
        if (nextIndex >= photos.length) {
          return loop ? 0 : prev
        }
        return nextIndex
      })
    }, slideInterval)
    return () => clearInterval(interval)
  }, [photos.length, event])

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-black text-white">
        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
      </div>
    )
  }

  if (photos.length === 0) {
    return (
      <div 
        className="flex h-screen flex-col items-center justify-center text-white p-12 text-center"
        style={{ backgroundColor: event?.kiosk?.backgroundColor || '#000000' }}
      >
        <h1 className="text-4xl font-black italic tracking-tighter opacity-20 mb-4">NO PHOTOS YET</h1>
        <p className="text-white/40 uppercase tracking-[0.3em]">Start capturing to see them here</p>
      </div>
    )
  }

  const currentPhoto = photos[currentIndex]

  // Transition variants
  const effect = event?.kiosk?.transitionEffect || 'fade'
  const getVariants = () => {
    if (effect === 'zoom') {
      return {
        initial: { opacity: 0, scale: 1.1 },
        animate: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 },
        transition: { duration: 1.2, ease: [0.22, 1, 0.36, 1] }
      }
    }
    if (effect === 'slide') {
      return {
        initial: { opacity: 0, x: 200 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: -200 },
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
      }
    }
    // Default 'fade'
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 1.0, ease: "easeInOut" }
    }
  }

  const variants = getVariants()

  return (
    <div 
      className={cn("relative h-screen w-screen overflow-hidden transition-all", isFullscreen ? "cursor-none" : "cursor-default")}
      style={{ backgroundColor: event?.kiosk?.backgroundColor || '#000000' }}
    >
      {/* Fullscreen Button */}
      {!isFullscreen && (
        <button 
          onClick={toggleFullscreen}
          className="absolute top-8 right-8 z-50 p-4 rounded-full bg-white/5 border border-white/10 text-white/40 hover:bg-white/10 hover:text-white transition-all shadow-md"
          title="Go Fullscreen"
        >
          <Maximize size={24} strokeWidth={3} />
        </button>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={currentPhoto.id}
          initial={variants.initial}
          animate={variants.animate}
          exit={variants.exit}
          transition={variants.transition as any}
          className="absolute inset-0 flex items-center justify-center"
        >
          {event?.kiosk?.blurBackground !== false && (
            <div 
              className="absolute inset-0 scale-110 blur-3xl opacity-30 pointer-events-none"
              style={{ 
                backgroundImage: `url(${currentPhoto.url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          )}
          
          <img
            src={currentPhoto.url}
            alt=""
            className={cn(
              "relative z-10 shadow-[0_0_100px_rgba(0,0,0,0.8)] object-contain transition-all duration-1000",
              (event?.kiosk?.showEventName || event?.kiosk?.showQrCode) 
                ? "max-h-[85vh] max-w-[85vw] rounded-xl" 
                : "max-h-screen max-w-screen rounded-none"
            )}
          />
        </motion.div>
      </AnimatePresence>

      {event?.kiosk?.showEventName && (
        <>
          <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-black/90 to-transparent pointer-events-none" />
          <div className="absolute bottom-12 left-12 z-20 flex flex-col gap-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-white/30 italic">Now Showing</div>
            <div className="text-3xl font-black italic text-white/90 tracking-tight uppercase truncate max-w-2xl">
              {event.name}
            </div>
          </div>
        </>
      )}

      {event?.kiosk?.showQrCode && qrData && (
        <div className="absolute bottom-12 right-12 z-20 flex flex-col items-end gap-3">
          <div className="flex flex-col items-end mr-2">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">Scan for Gallery</span>
          </div>
          <div className="rounded-xl bg-white p-3 shadow-md">
            <img src={qrData} alt="Gallery QR" className="h-24 w-24" />
          </div>
        </div>
      )}
      
      <div className="absolute bottom-0 left-0 h-1 bg-white/5 w-full z-30 opacity-40">
        <motion.div
          key={currentIndex}
          initial={{ width: 0 }}
          animate={{ width: "100%" }}
          transition={{ duration: event?.kiosk?.interval || 10, ease: "linear" }}
          className="h-full bg-white/40"
        />
      </div>
    </div>
  )
}
